// The submission flow's URL contract.
//
// Every flow URL carries the breakdown as `bd` (required) and, optionally, the
// role as `roleid` — matching the `bd` convention the rest of the app already
// uses for breakdown links (see Routes.tsx's /register/special/actor handler).
//
// Ids are kept as RAW STRINGS on purpose. Real breakdown refs arrive
// zero-padded (bd=0175445) and nothing in the flow does arithmetic on them, so
// parsing to a number would only risk changing the value we send back.

import qs from "query-string";

export const BD_PARAM = "bd";
export const ROLE_PARAM = "roleid";

// camelCase spellings are accepted so links written against the earlier
// internal contract keep working; `bd`/`roleid` are what we emit.
const BD_ALIASES = [BD_PARAM, "breakdownId", "breakdownid"];
const ROLE_ALIASES = [ROLE_PARAM, "roleId", "roleID"];

export interface ISubmissionParams {
  bd: string | null;
  roleid: string | null;
}

const firstOf = (params: any, keys: string[]): string | null => {
  for (const key of keys) {
    const raw = params[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
};

export const parseSubmissionParams = (search?: string): ISubmissionParams => {
  try {
    const params = qs.parse(search || "");
    return {
      bd: firstOf(params, BD_ALIASES),
      roleid: firstOf(params, ROLE_ALIASES),
    };
  } catch (e) {
    return { bd: null, roleid: null };
  }
};

// The breakdown and role a flow page is for. The link wins when it names a
// breakdown: a step opened from a link (the login's hand-back, an email) is for
// that one, and what this tab remembers from an earlier submission must not
// replace it. Reported 2026-10-29: back from the login for role B, step 2
// showed role A's breakdown and made a draft with no breakdown at all, so step
// 4 stopped at "Pick a role first." Without one on the link, the store's.
export const flowRefs = (
  stored: { breakdownId: string | null; roleId: string | null },
  search?: string
): { breakdownId: string | null; roleId: string | null } => {
  const link = parseSubmissionParams(search);
  if (link.bd && link.bd !== stored.breakdownId) {
    return { breakdownId: link.bd, roleId: link.roleid };
  }
  return {
    breakdownId: stored.breakdownId || link.bd,
    roleId: link.roleid || stored.roleId,
  };
};

// The search string with one parameter set (or removed, with null) and every
// other parameter kept, so a hop that adds its own flag (?from=) does not drop
// the breakdown, the role or the step-4 pick on the way.
export const withParam = (
  search: string | undefined,
  key: string,
  value: string | null
): string => {
  const params = new URLSearchParams(search || "");
  if (value === null) params.delete(key);
  else params.set(key, value);
  const out = params.toString();
  return out ? `?${out}` : "";
};

// Rebuilds a search string that keeps every incoming param (campaign tags, the
// mock switches) but guarantees bd/roleid are present and canonically spelled,
// so the pair survives every hop through the wizard.
export const buildFlowSearch = (
  search: string | undefined,
  bd: string | null,
  roleid?: string | null
): string => {
  let params: any = {};
  try {
    params = qs.parse(search || "") || {};
  } catch (e) {
    params = {};
  }
  // Drop the aliases so we never emit both spellings of the same id.
  BD_ALIASES.concat(ROLE_ALIASES).forEach((key) => delete params[key]);
  if (bd) params[BD_PARAM] = bd;
  if (roleid) params[ROLE_PARAM] = roleid;
  const qsOut = qs.stringify(params);
  return qsOut ? `?${qsOut}` : "";
};
